/**
 * Setup readiness card copy. Express decides each area's state; this only
 * words it. Never says or implies the venue is compliant.
 */

import moment from 'moment'
import {
  FOOD_SAFETY_READINESS_AREA,
  FOOD_SAFETY_READINESS_COPY,
  FOOD_SAFETY_READINESS_STATE,
  FOOD_SAFETY_READINESS_STATE_LABELS,
  FOOD_SAFETY_SECTION,
} from 'src/constants/foodSafety'

const STATE = FOOD_SAFETY_READINESS_STATE
const SECTION = FOOD_SAFETY_SECTION

export const FOOD_SAFETY_READINESS_STAFF_LINK_LABEL = 'See what staff see'
export const FOOD_SAFETY_READINESS_STAFF_LINK_HINT =
  'Opens today’s checks — the screen staff use day to day.'
export const FOOD_SAFETY_READINESS_CONFIRM_LABEL =
  'Review & confirm Food Safety setup'
export const FOOD_SAFETY_READINESS_CONFIRM_RECORD_NOTE =
  'Confirming records who reviewed this setup and when, so later changes show up here.'

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

const AREA_COUNT_NOUNS = Object.freeze({
  [SECTION.FRIDGES_FREEZERS]: ['unit', 'units'],
  [SECTION.CLEANING_CHECKLISTS]: ['checklist', 'checklists'],
  [SECTION.PROBE_CALIBRATION]: ['probe', 'probes'],
  [SECTION.DISHWASHERS]: ['dishwasher', 'dishwashers'],
  [FOOD_SAFETY_READINESS_AREA.REPORT_RECIPIENTS]: ['recipient', 'recipients'],
})

/** ok / attention / todo / muted — for the state pill. */
export function readinessStateTone(state) {
  if (state === STATE.CONFIGURED) return 'ok'
  if (state === STATE.NEEDS_ATTENTION) return 'attention'
  if (state === STATE.NOT_CONFIGURED) return 'todo'
  return 'muted'
}

export function readinessStateLabel(area) {
  return (
    area?.stateLabel || FOOD_SAFETY_READINESS_STATE_LABELS[area?.state] || ''
  )
}

export const isReadinessStaffPreviewArea = area =>
  area?.key === FOOD_SAFETY_READINESS_AREA.STAFF_PREVIEW

/** "3 units" / "2 saved" / "Venue range saved" — or null when nothing to say. */
export function formatReadinessAreaDetail(area) {
  if (!area || isReadinessStaffPreviewArea(area)) return null
  const count = Number(area.count) || 0
  if (count > 0) {
    const nouns = AREA_COUNT_NOUNS[area.key]
    return nouns ? plural(count, nouns[0], nouns[1]) : `${count} saved`
  }
  if (area.detail?.range?.source === 'venue') return 'Venue range saved'
  if (area.detail?.coolingTarget?.source === 'venue') {
    return 'Venue target saved'
  }
  if (area.state === STATE.OPTIONAL) return 'Staff can still log these by hand'
  return null
}

/** First issues on an area, plus "+N more". */
export function readinessAreaIssueLines(area, { max = 2 } = {}) {
  const issues = Array.isArray(area?.issues) ? area.issues : []
  const lines = issues
    .slice(0, max)
    .map(i => i?.message)
    .filter(Boolean)
  if (issues.length > max) lines.push(`+${issues.length - max} more`)
  return lines
}

export function formatReadinessConfirmedBy(confirmation, { now } = {}) {
  if (!confirmation?.confirmedAt) return null
  const at = moment(confirmation.confirmedAt)
  if (!at.isValid()) return null
  const sameYear = at.isSame(moment(now), 'year')
  const when = at.format(sameYear ? 'D MMM, HH:mm' : 'D MMM YYYY, HH:mm')
  const who = String(confirmation.confirmedByName || '').trim()
  return who ? `Confirmed by ${who} · ${when}` : `Confirmed ${when}`
}

/** "2 areas to set up · 1 needs attention" — or null when nothing is left. */
export function formatReadinessOutstanding(counts = {}) {
  const parts = []
  const todo = Number(counts.notConfigured) || 0
  const attention = Number(counts.needsAttention) || 0
  if (todo) parts.push(`${plural(todo, 'area')} to set up`)
  if (attention) {
    parts.push(`${attention} need${attention === 1 ? 's' : ''} attention`)
  }
  return parts.length ? parts.join(' · ') : null
}

/**
 * Headline + supporting line for the card.
 * `tone`: done (complete + confirmed + unchanged) / changed / todo.
 */
export function formatReadinessStatus(readiness, { now } = {}) {
  const summary = readiness?.summary || {}
  const confirmedBy = formatReadinessConfirmedBy(readiness?.lastConfirmation, {
    now,
  })
  const outstanding = formatReadinessOutstanding(summary.counts)
  if (summary.message) {
    return {
      tone: 'done',
      headline: summary.message,
      detail: confirmedBy,
    }
  }
  if (summary.status === 'changed_since_confirmed') {
    return {
      tone: 'changed',
      headline: 'Setup changed since it was last confirmed',
      detail: [confirmedBy, outstanding].filter(Boolean).join(' · ') || null,
    }
  }
  if (summary.status === 'confirmed') {
    return {
      tone: 'todo',
      headline: outstanding || 'Confirmed',
      detail: confirmedBy,
    }
  }
  return {
    tone: 'todo',
    headline: summary.ready
      ? 'Ready to confirm'
      : outstanding || 'Not confirmed yet',
    detail: summary.ready
      ? 'Every area is set up. Review it, then confirm.'
      : 'Not confirmed yet.',
  }
}

/** Confirm dialog body — what's being recorded, never "compliant". */
export function readinessConfirmDialogLines(readiness) {
  const counts = readiness?.summary?.counts || {}
  const lines = [FOOD_SAFETY_READINESS_CONFIRM_RECORD_NOTE]
  const todo = Number(counts.notConfigured) || 0
  const attention = Number(counts.needsAttention) || 0
  if (todo || attention) {
    const parts = []
    if (todo) parts.push(`${plural(todo, 'area')} not configured`)
    if (attention) parts.push(`${attention} needing attention`)
    lines.push(
      `${parts.join(
        ' and ',
      )} — they’ll be recorded as they are. You can confirm again after fixing them.`,
    )
  }
  lines.push(
    readiness?.summary?.responsibilityNote ||
      FOOD_SAFETY_READINESS_COPY.responsibility,
  )
  return lines
}

/** Setup tab an area opens, or null (recipients / staff preview). */
export function readinessAreaSetupSection(area, visibleSections = []) {
  const key = area?.key
  return visibleSections.includes(key) ? key : null
}
